import { isFeatureEnabled } from '@lumapps/features';
import { isSuperAdmin } from '@lumapps/permissions';
import { createSelector } from '@lumapps/redux/reselect';
import type { FrontOfficeStore } from '@lumapps/redux/stores/frontOffice';

import { AGENT_HUB_FEATURE_TOKEN, AGENT_HUB_BETA_FEATURE_TOKEN, AGENT_HUB_ALPHA_FEATURE_TOKEN } from '../constants';
import { AgentHubState } from '../types';

/** Check whether the AgentHub FF is enabled in the store */
export const isAgentHubEnabled = isFeatureEnabled(AGENT_HUB_FEATURE_TOKEN);

/** Check whether the AgentHub alpha FF is enabled */
export const isAgentHubAlphaEnabled = isFeatureEnabled(AGENT_HUB_ALPHA_FEATURE_TOKEN);

/** Check whether the AgentHub beta FF is enabled */
export const isAgentHubBetaEnabled = isFeatureEnabled(AGENT_HUB_BETA_FEATURE_TOKEN);

/** Check whether the AgentHub FFs have at least beta enabled. */
export const isAgentHubAlphaOrBetaEnabled = createSelector(
    isAgentHubEnabled,
    isAgentHubAlphaEnabled,
    isAgentHubBetaEnabled,
    (hasMain, hasAlpha, hasBeta) => hasMain && (hasAlpha || hasBeta),
);

/** Check whether the Agent Library is enabled (alpha OR beta) */
export const isAgentLibraryEnabled = createSelector(
    isAgentHubAlphaEnabled,
    isAgentHubBetaEnabled,
    (hasAlpha, hasBeta) => hasAlpha || hasBeta,
);

export const isAnyAgentHubVersionEnabledSelector = createSelector(
    isAgentHubEnabled,
    isAgentHubAlphaEnabled,
    isAgentHubBetaEnabled,
    (hasAgentHub, hasAlpha, hasBeta) => hasAgentHub || hasAlpha || hasBeta,
);

/**
 * Retrieves whether the user can access the agent hub admin.
 * For the moment, the agent hub admin view is accessible to both its dedicated FF or if ask is in alpha
 * This is because there will be a transition period
 */
export const canAccessAgentHubAdminSelector = createSelector(
    isAnyAgentHubVersionEnabledSelector,
    isSuperAdmin,
    (agentHubEnabled, userIsSuperAdmin) => {
        return userIsSuperAdmin && agentHubEnabled;
    },
);

/**
 * Whether the agent hub compliance page should be rendered.
 * Can be deleted once available to all.
 */
export const isAgentHubCompliancePageEnabledSelector = isAgentHubBetaEnabled;

/**
 * Whether the agent conversation starters feature is enabled.
 * Can be deleted once available to all.
 */
export const areAgentConversationStartersEnabledSelector = createSelector(
    isAgentHubAlphaEnabled,
    isAgentHubBetaEnabled,
    (hasAlpha, hasBeta) => hasAlpha || hasBeta,
);

/** Retrieves whether the user can access the agent hub compliance admin page */
export const canAccessAgentHubComplianceAdminSelector = createSelector(
    isAgentHubCompliancePageEnabledSelector,
    isSuperAdmin,
    (agentHubEnabled, userIsSuperAdmin) => {
        return userIsSuperAdmin && agentHubEnabled;
    },
);

const agentHub = (state: FrontOfficeStore): AgentHubState | undefined => state.agentHub;

export const isChatOpenedSelector = createSelector(agentHub, (agentHub) => agentHub?.isChatOpen);

export const selectedChatAgentSelector = createSelector(agentHub, (agentHub) => agentHub?.selectedAgent);
export const pendingAgentChangeSelector = createSelector(agentHub, (agentHub) => agentHub?.pendingAgentChange);
